/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { type FileExt, formatDate } from '@poweralpha/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAuthHeader } from '../../api';
import { downloadFile, type ErrorResponse } from '../../utils';

export const getHeaders = async () => {
  const authHeader = await getAuthHeader();
  return { authorization: authHeader };
};

export const getQueryStr = (query: Record<string, string> | undefined) => {
  if (!query) {
    return '';
  }

  const searchParams = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    searchParams.append(key, value);
  });

  const baseQuery = searchParams.toString();
  return baseQuery ? `?${baseQuery}` : '';
};

interface FileDownloadReturn {
  isDownloading: boolean;
  progress: number;
  error?: string;
  startDownload: () => void;
}

export function useFileDownload(
  path: string,
  options?: {
    baseFileName?: string;
    ext?: FileExt | null;
    method?: 'GET' | 'POST';
    apiVersion?: 'v1' | 'v2';
    useServerFilename?: boolean;
    query?: Record<string, string>;
    json?: unknown;
    errorHandler?: (err: ErrorResponse) => void;
    onComplete?: () => void;
  },
): FileDownloadReturn {
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string>();
  // Track mounted state manually since the callback kicks off an async
  // process which makes state updates. We must avoid updating state if no
  // longer mounted, but can continue the file download for the user even
  // if they change page.
  const mounted = useRef(true);

  const queryStr = useMemo(() => getQueryStr(options?.query), [options?.query]);
  const downloadPath = useMemo(() => path.replace(/^\/+/, ''), [path]);

  const startDownload = useCallback(() => {
    let downloadFilename = options?.baseFileName
      ? `${options?.baseFileName}-${formatDate()}`
      : undefined;
    setError(undefined);
    setIsDownloading(true);
    setProgress(0);

    // Need to use raw fetch API here instead of ky in order to correctly get file headers
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: 33/15 complexity
    async function download(): Promise<void> {
      const authHeader = await getAuthHeader();
      const isPost = (options?.method ?? 'GET') === 'POST';

      const response = await fetch(
        `/api/${options?.apiVersion ?? 'v1'}/${downloadPath}${queryStr}`,
        {
          method: options?.method ?? 'GET',
          headers: {
            authorization: authHeader,
            ...(isPost ? { 'content-type': 'application/json' } : {}),
          },
          body: isPost && options?.json ? JSON.stringify(options.json) : undefined,
        },
      );

      if (response.status !== 200) {
        let message = response.statusText;
        let error = 'Error';

        try {
          const rawResponse = (await response.text()) || '';
          if (rawResponse) {
            const parsed = JSON.parse(rawResponse) as Partial<ErrorResponse>;
            error = parsed.error ?? error;
            message = parsed.message ?? message;
          }
        } catch {
          // Fall back to the HTTP status text when the error response is not JSON.
        }

        throw { error, message, statusCode: response.status } satisfies ErrorResponse;
      }

      if (options?.useServerFilename || !downloadFilename) {
        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = contentDisposition?.match(/filename="?([^"]+)"?/)?.[1];
        if (filename) {
          downloadFilename = filename;
        }
      }

      // track download progress
      const contentLength = Number.parseInt(response.headers.get('Content-Length') ?? '', 10);
      const reader = response.body?.getReader();

      if (reader === undefined) {
        throw new Error('No body received');
      }

      let receivedLength = 0;
      const chunks: BlobPart[] = [];
      let loading = true;

      while (loading) {
        const { done, value } = await reader.read();

        if (done) {
          loading = false;
        }

        if (value !== undefined) {
          chunks.push(value);
          receivedLength += value.length;
        }

        const progressPct = receivedLength / contentLength;
        if (Number.isFinite(progressPct)) {
          setProgress(progressPct * 100);
        }
      }

      const blob = new Blob(chunks);
      downloadFile(
        blob,
        downloadFilename ?? 'unknown',
        options?.useServerFilename ? null : (options?.ext ?? null),
      );
    }

    download()
      .catch((e) => {
        if (mounted.current) {
          setError((e as ErrorResponse).message);
          if (options?.errorHandler) {
            options.errorHandler(e as ErrorResponse);
          }
        }
      })
      .finally(() => {
        if (mounted.current) {
          // Avoid setState if we're no longer mounted
          setIsDownloading(false);
          options?.onComplete?.();
        }
      });
  }, [downloadPath, options, queryStr]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return {
    isDownloading,
    progress,
    error,
    startDownload,
  };
}
